import React, { useEffect, useState } from "react";
import { listClients, createClient, updateClient, deleteClient, uploadClientUsers } from "../api/clientApi";
import ClientModal from "../components/ClientModal";

function ClientMasterPage({ authHeaders, setToast }) {
  const STORAGE_KEY = "client_master_list";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadClientId, setUploadClientId] = useState(null);
  const [uploadPassword, setUploadPassword] = useState("");
  const usingServer = Boolean(authHeaders);

  const loadClients = async () => {
    if (usingServer) {
      setLoading(true);
      setError("");
      try {
        const list = await listClients(authHeaders);
        setClients(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || "Failed to load clients");
      } finally {
        setLoading(false);
      }
      return;
    }

    // localStorage fallback
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setClients(Array.isArray(parsed) ? parsed : []);
    } catch (err) {
      setClients([]);
    }
  };

  useEffect(() => {
    loadClients();
  }, [authHeaders]);

  const persistLocal = (next) => {
    setClients(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const handleCreateFromModal = async (payload) => {
    if (usingServer) {
      const created = await createClient(payload, authHeaders);
      setClients((prev) => [created, ...prev]);
      setToast?.("Client created");
      return created;
    }
    const created = { id: Date.now(), ...payload };
    persistLocal([created, ...clients]);
    setToast?.("Client created");
    return created;
  };

  const handleUpdateFromModal = async (id, payload) => {
    if (usingServer) {
      const updated = await updateClient(id, payload, authHeaders);
      setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setToast?.("Client updated");
      return updated;
    }
    const next = clients.map((c) => (c.id === id ? { ...c, ...payload } : c));
    persistLocal(next);
    setToast?.("Client updated");
    return next.find((c) => c.id === id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    if (usingServer) {
      try {
        await deleteClient(id, authHeaders);
        setClients((prev) => prev.filter((c) => c.id !== id));
        setToast?.("Client removed");
      } catch (err) {
        console.error(err);
        setToast?.("Failed to remove client");
      }
      return;
    }
    const next = clients.filter((c) => c.id !== id);
    persistLocal(next);
    setToast?.("Client removed");
  };

  return (
    <section className="admin-users">
   <header className="admin-users-header">

  {/* LEFT TITLE */}
  <div className="admin-users-title">
    <h2>Client Management</h2>
  </div>

  {/* RIGHT ACTIONS */}
  <div className="admin-users-actions">

    {/* ADD BUTTON */}
    <button
      type="button"
      onClick={() => {
        setEditing(null);
        setIsModalOpen(true);
      }}
    >
      Add New
    </button>


    {/* DOWNLOAD EXCEL FORMAT BUTTON */}
    <button
      type="button"
      onClick={() => {
        // Download Excel/CSV template for user upload (Email, Full Name, Mobile No)
        const headers = ["UserEmailId", "Full Name", "Mobile No"];
        const rows = [headers, ["user@email.com", "User Name", "9876543210"]];
        const csvContent = [
          '"Fill all columns. Do not include password. Enter password in the textbox when uploading."',
          rows.map(r => r.join(",")).join("\r\n")
        ].join("\r\n\r\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "client-users-template.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }}
    >
      Download User Excel Format
    </button>

  </div>
</header>

      <div className="admin-users-grid">
        <div className="admin-users-list" style={{ width: "100%" }}>
          <h2>Clients</h2>
          {error && <div className="admin-users-error">{error}</div>}
          {loading && !clients.length ? (
            <div className="mail-empty">Loading clients…</div>
          ) : clients.length === 0 ? (
            <div className="mail-empty">No clients found.</div>
          ) : (
            <div className="admin-users-table-wrapper">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Contact no</th>
                    <th>Address</th>
                    <th>Actions</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{c.contact || "—"}</td>
                      <td>{c.address || "—"}</td>
                      <td>
                        <button className="mail-top-btn" onClick={() => { setEditing(c); setIsModalOpen(true); }}>Edit</button>
                      </td>
                      <td>
                        {/* Per-client upload: show password input and upload button inline */}
                        <form style={{ display: "flex", alignItems: "center", gap: 4 }} onSubmit={async (e) => { e.preventDefault(); }}>
                          <input
                            type="password"
                            placeholder="Excel Password"
                            value={uploadClientId === c.id ? uploadPassword : ""}
                            style={{ width: 120 }}
                            onChange={e => {
                              setUploadClientId(c.id);
                              setUploadPassword(e.target.value);
                            }}
                          />
                          <button
                            type="button"
                            className="mail-top-btn"
                            onClick={() => {
                              setUploadClientId(c.id);
                              document.getElementById(`client-user-upload-${c.id}`)?.click();
                            }}
                          >Upload Users</button>
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            id={`client-user-upload-${c.id}`}
                            style={{ display: "none" }}
                            onChange={async (e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file || !uploadClientId) return;
                              try {
                                setLoading(true);
                                const result = await uploadClientUsers(uploadClientId, file, authHeaders, uploadPassword);
                                setToast?.(
                                  `Uploaded: ${result?.results?.created || 0} created, ${
                                    result?.results?.skipped || 0
                                  } skipped`
                                );
                                await loadClients();
                              } catch (err) {
                                setToast?.(
                                  err.response?.data?.error || err.message || "Upload failed"
                                );
                              } finally {
                                setLoading(false);
                                e.target.value = "";
                                setUploadClientId(null);
                                setUploadPassword("");
                              }
                            }}
                          />
                        </form>
                      </td>
                      <td>
                        <button className="mail-top-btn" onClick={() => handleDelete(c.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ClientModal
        isOpen={isModalOpen}
        initial={editing}
        onClose={(result) => {
          setIsModalOpen(false);
          setEditing(null);
        }}
        onCreate={async (payload) => await handleCreateFromModal(payload)}
        onUpdate={async (id, payload) => await handleUpdateFromModal(id, payload)}
      />
    </section>
  );
}

export default ClientMasterPage;
